<script lang="ts">
    import { slide } from "svelte/transition";
    import { navigate, useParams } from "svelte-navigator";
    import { Accordion, AccordionItem, Badge, Button, Col, Container, Icon, Offcanvas, Row, Spinner } from "sveltestrap";
    import BackBtn from "../Components/BackBtn.svelte";
    import TopBar from "../Components/TopBar.svelte";
    import type { Form } from "../Model/Form";
    import type { FormInstance } from "../Model/FormInstance";
    import { answersFactory, answersService, eventsChannelFactory, formsService, formValidatorFactory, instancesService, notificationsService, sessionService } from "../Services";
    import { Answer } from "../Model/Answer";
    import type { Question } from "../Model/Question";
    import { onDestroy, setContext } from "svelte";
    import type { EventsChannel } from "../Services/EventsChannel";
    import { readable, writable } from "svelte/store";
    import MembersList from "../Components/MembersList.svelte";
    import type { AppEvent } from "../global";
    import SignaturesCount from "../Components/SignaturesCount.svelte";
    import FinishBtn from "../Components/FinishBtn.svelte";
    import SectionAccordionItem from "../Components/Form/SectionAccordionItem.svelte";
    import type { FormMember } from "../Model/FormMember";
    import SignForm from "../Components/SignForm.svelte";
    import Watch from "../Components/Watch.svelte";
    import type { FormValidator } from "../Services/FormValidator";

    const params = useParams();
    const _readonlyForm = writable<boolean>(false);
    const readonlyForm = readable<boolean>(false, set => _readonlyForm.subscribe(set));
    const _isFulfilled = writable<boolean>(false);
    const isFulfilled = readable<boolean>(false, set => _isFulfilled.subscribe(set));
    const instanceWritable = writable<FormInstance | undefined>(undefined);
    const instanceReadable = readable<FormInstance | undefined>(undefined, set => instanceWritable.subscribe(set));
    const questionsMap = new Map<string, Question>();
    const answersMap = new Map<string, Answer>();

    let instanceId: string = '';
    let instance: FormInstance;
    let member: FormMember;
    let form: Form;
    let isLoading = false;
    let isOwner = false;
    let loadComplete = false;
    let openEndOffcanvas = false;
    let isDeleting = false;
    let showUsers = false;
    let instanceChannel: EventsChannel;
    let formValidator: FormValidator;

    setContext('getAnswer', getAnswer);
    setContext('readonlyForm', readonlyForm);
    setContext('isFulfilled', isFulfilled);
    setContext('saveAnswer', saveAnswer);
    setContext('instance', instanceReadable);
    setContext('updateValidationState', updateIsFulfilledState);

    $: paramsChange($params);

    onDestroy(() => {
        instanceChannel?.destroy();
        instance?.destroy();
    });

    function paramsChange(params: any) {
        if (!params.instanceId || params.instanceId === instanceId) {
            return;
        }

        instanceId = params.instanceId;

        const offline = localStorage.getItem('instanceOffline')

        if (offline && offline == instanceId) {
            localStorage.removeItem('instanceOffline');
        }


        getInstance(instanceId);
    }

    async function getInstance(instanceId: string): Promise<void> {
        try {

            instanceChannel?.destroy();

            loadComplete = false;
            isLoading = true;
            instance = await instancesService.getInstance(instanceId);
            
            if (!instance) {
                return;
            }

            if (!instance.hasMember(sessionService.user.id)) {
                //El usuario actual no es miembro de este formulario
                navigate('/inicio');
                return;
            }

            $instanceWritable = instance;
            isOwner = instance.hasOwner(sessionService.user.id);
            member = instance.getMember(sessionService.user.id);
            $_readonlyForm = !isOwner;
            instanceChannel = eventsChannelFactory.createEventsChannel(instance.id);

            instanceChannel.on('answer', onAnswer);
            instanceChannel.on('removed', onRemoved);
            instanceChannel.on('member_remove', onMemberRemove);

            let answers: Answer[];

            [form, answers] = await Promise.all([
                formsService.getForm(instance.formId),
                answersService.getInstanceAnsers(instanceId),
            ]);
            
            answersMap.clear();
            questionsMap.clear();

            answers.forEach(answer => answersMap.set(answer.questionId, answer));
            form.getAllQuestions().forEach(question => questionsMap.set(question.id, question));

            formValidator = formValidatorFactory.createValidator(
                form,
                instance,
                answersMap
            );

            await updateIsFulfilledState();

            loadComplete = true;

        } catch (error) {
            console.error(error);
        } finally {
            isLoading = false;
        }
    }

    async function saveAnswer(answer: Answer): Promise<void> {
        await answersService.saveAnswer(answer);
        await updateIsFulfilledState();
    }

    async function onAnswer({ data }: AppEvent): Promise<void> {
        //Creamos la respuesta si no existe
        if (!answersMap.has(data.questionId)) {
            const answer = Answer.fromPrimitives(data);
            answersMap.set(answer.questionId, answer);
        }
        //Actualizamos la respuesta
        answersMap.get(data.questionId).value = data.value;
        await updateIsFulfilledState();
    }

    function onRemoved(): void {
        //La instancia ha sido eliminada
        if (isDeleting) {
            return;
        }
        notificationsService.success('Eliminado', 'El formulario ha sido eliminado.');
        navigate('/inicio');
    }

    function onMemberRemove({ data: userId }: AppEvent<string>): void {
        if (userId === sessionService.user.id) {
            //Se ha eliminado al usuario actual como miebro del formulario
            notificationsService.notify('Miembro retirado', 'Ha sido retirado como miembro del formulario.');
            navigate('/inicio');
        }
    }

    async function updateIsFulfilledState(): Promise<void> {
        $_isFulfilled = await formValidator.validate();
    }

    function getAnswer(questionId: string): Answer {
        if (!answersMap.has(questionId)) {
            const question = questionsMap.get(questionId);
            const answer = answersFactory.createAnswer(question, instance.id);
            answersMap.set(questionId, answer);
        }
        return answersMap.get(questionId);
    }

    function toggleEndOffcanvas() {
        openEndOffcanvas = !openEndOffcanvas;
    }

    async function onRemoveClick() {
        if (isDeleting || !confirm('Se eliminará el formulario y todas las respuestas proporcionadas. Esto no se puede deshacer.\n¿Está seguro de continuar?')) {
            return;
        }

        try {
            isDeleting = true;
            await instancesService.removeInstance(instanceId);
            notificationsService.success('Eliminado', 'El formulario ha sido eliminado correctamente.');
            navigate('/inicio');
        } catch (error) {
            console.error(error);
            notificationsService.error('Error', 'Ha ocurrido un error al eliminar el formulario.');
            isDeleting = false;
        }
    }

    function toggleUsers() {
        showUsers = !showUsers;
    }

    async function onRemoveSign(): Promise<void> {
        if (!confirm('¿Seguro que desea eliminar su firma de este formulario?')) {
            return;
        }
        try {
            await instancesService.unsingInstance(instance.id, member.userId);
            member.setUnsigned();
        } catch (error) {
            console.log(error);
        }
    }

</script>

<style lang="scss">

    @use '../variables';

    .view {
        display: flex;
        flex-direction: column;
    }

    .content {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        max-height: calc(100% - 120px);
        overflow: auto;
        background-color: #fff;
    }

</style>

<div class="view">
    <TopBar title={form?.name || "Formulario"}>
        <BackBtn slot="left" href="/inicio" />
        <div slot="right">
            {#if isOwner}
            <Button color="light" slot="right" on:click={toggleEndOffcanvas}>
                <Icon name="three-dots-vertical" />
            </Button>
            {/if}
        </div>
    </TopBar>

    <div class="content">

        {#if isLoading}
        <div class="d-flex justify-content-center my-4">
            <Spinner color="primary" />
        </div>    
        {/if}

        {#if loadComplete}

        <Container class="bg-white shadow py-3 mb-4">
            <Row>
                <Col>
                    <h2 class="m-0">{form.name}</h2>
                    <div class="d-flex gap-1 align-items-center">
                        
                        {#if isOwner}
                            <small>
                                <Badge color="primary"><Icon name="person-circle" /> Propietario</Badge>
                            </small>
                        {/if}

                        {#if $isFulfilled}
                            <small>
                                <Badge color="success"><Icon name="check2" /> Completado</Badge>
                            </small>
                        {/if}

                        <SignaturesCount {instance} />
                        
                        <div class="flex-grow-1 text-end">
                            <Button class="py-1 px-2" color={showUsers ? 'primary' : 'secondary'} size="sm" on:click={toggleUsers}>
                                <Icon name="people-fill" />
                            </Button>
                        </div>
                    </div>
                    
                    <p>{form.description}</p>

                    {#if showUsers}
                    <div transition:slide>
                        <MembersList {instance} />
                    </div>
                    {/if}
                </Col>
            </Row>
        </Container>

        <Accordion flush>

            {#each form.sections as section, index}
                <SectionAccordionItem
                    instanceId={instance.id}
                    sectionInstance={instance.sections.get(section.id)}
                    {section}
                    number={index + 1} />
            {/each}

            <AccordionItem>
                <h5 class="m-0" slot="header">
                    Finalizar
                    {#if $isFulfilled}
                        <Badge color="success" class="rounded-pill">
                            <Icon name="check" class="text-white" />
                        </Badge>
                    {/if}
                </h5>
                <Watch store={[instance.isFinishedReadable, member.isSignedReadable]} let:value={[isFinished, isSigned]}>
                    {#if isFinished || isSigned}
                        <p class="d-flex justify-content-between">
                            <span>
                                <Icon name="patch-check" /> Firmado
                            </span>
                            {#if !isFinished}
                            <Button type="button" color="danger" size="sm" on:click={onRemoveSign}>
                                <Icon name="x" /> Quitar firma
                            </Button>
                            {/if}
                        </p>
                    {:else}
                        <SignForm {member} />
                    {/if}
                </Watch>
                <div class="mt-3">
                    <FinishBtn {instance} {answersMap} {form} />
                </div>
            </AccordionItem>

        </Accordion>

        {/if}
    </div>

</div>

{#if isOwner}
<Offcanvas isOpen={openEndOffcanvas} toggle={toggleEndOffcanvas} placement="end" header="Opciones" style="width: 300px">
    <Button color="danger" block on:click={onRemoveClick}><Icon name="x-octagon-fill" /> Eliminar</Button>
</Offcanvas>
{/if}

